import type { Schemas } from '@seoai/api-types';
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../auth/session';
import { request, requestPaginated, type Paginated } from './client';

/**
 * Sorgu anahtarları TEK yerde ve AKTİF KURUMU içerir (SEOAI-78).
 *
 * Kurum kimliği anahtarın parçası olmasaydı, kurum değiştirince önceki kurumun
 * verisi önbellekten gelirdi — çok kiracılı bir üründe en pahalı hata budur.
 * Anahtarda olduğu için geçiş, ayrıca bir invalidate çağrısı gerektirmeden
 * doğru veriyi getirir; yine de switch sonrası önbellek temizlenir (aşağıda).
 */
export const queryKeys = {
  locales: () => ['locales'] as const,
  me: (tenantId: string | null) => ['me', tenantId] as const,
  permissions: (tenantId: string | null) => ['permissions', tenantId] as const,
  tenants: () => ['tenants'] as const,
  platformTenants: (page: number) => ['platform', 'tenants', page] as const,
  projects: (tenantId: string | null) => ['projects', tenantId] as const,
  roles: (tenantId: string | null) => ['roles', tenantId] as const,
  project: (tenantId: string | null, id: string) => ['projects', tenantId, id] as const,
};

/**
 * Dil kataloğu — PUBLIC uç, oturum gerektirmez (SEOAI-78 incelemesi). Panel dil
 * listesini kopyalamaz; tek kaynak config/i18n.php (SEOAI-76).
 */
export function useLocaleCatalog(): UseQueryResult<Schemas['LocaleCatalogDTO']> {
  return useQuery({
    queryKey: queryKeys.locales(),
    queryFn: () => request<Schemas['LocaleCatalogDTO']>('/locales'),
    // Katalog nadiren değişir; her gezinmede yeniden çekmenin anlamı yok.
    staleTime: 60 * 60 * 1000,
  });
}

export function useMe(): UseQueryResult<Schemas['UserDTO']> {
  const tenantId = useSession((s) => s.tenantId);

  return useQuery({
    queryKey: queryKeys.me(tenantId),
    queryFn: () => request<Schemas['UserDTO']>('/auth/me'),
  });
}

/**
 * Kurum listesi — YALNIZCA gerçekten gerekliyse çekilir.
 *
 * `enabled` varsayılanı YOK: liste çeken iki yer var (kurum seçim kapısı ve
 * başlıktaki değiştirici) ve ikisi de ne zaman gerektiğini AÇIKÇA söylemeli.
 * Varsayılan `true` olduğu için değiştirici bunu koşulsuz çağırıyordu: kapı
 * turu atlıyordu ama başlık aynı isteği yine yapıyordu — biletin kaldırdığı
 * tur, bir satır yukarıdan geri geliyordu (SEOAI-80 ikinci inceleme).
 */
export function useMyTenants(enabled: boolean): UseQueryResult<Schemas['MyTenantDTO'][]> {
  return useQuery({
    queryKey: queryKeys.tenants(),
    queryFn: () => request<Schemas['MyTenantDTO'][]>('/me/tenants'),
    enabled,
  });
}

/**
 * İzinler store'a da yazılır: SEOAI-47 menü/route bağlamasını buradan okuyacak.
 * Bu bilette yalnızca VERİ hazırlanır, bağlama yapılmaz.
 */
/**
 * Platform kurum listesi (SEOAI-81) — YALNIZCA platform yöneticisi için.
 *
 * `enabled` zorunlu: normal kullanıcı için istek hiç atılmamalı. Yetki
 * sunucudadır, ama olmayan bir yetkinin isteğini atmak kullanıcıya 403
 * göstermekten başka işe yaramaz.
 */
export function usePlatformTenants(
  page: number,
  enabled: boolean,
): UseQueryResult<Paginated<Schemas['TenantDTO']>> {
  return useQuery({
    queryKey: queryKeys.platformTenants(page),
    queryFn: () => requestPaginated<Schemas['TenantDTO']>(`/platform/tenants?page=${page}&perPage=25`),
    enabled,
  });
}

/**
 * Kurum bağlamına geçiş (SEOAI-81). Yanıt normal bir jeton çiftidir; oturum
 * aynı kapıdan (applySession) yazılır — platform yolu ayrı olsa da oturumun
 * TEK bir yazma yeri vardır.
 *
 * Önbellek TÜMÜYLE sıfırlanır: ekrandaki her şey artık BAŞKA bir kurumun verisi.
 */
export function useEnterTenantContext() {
  const queryClient = useQueryClient();
  const applySession = useSession((s) => s.applySession);

  return useMutation({
    mutationFn: (tenantId: string) =>
      request<Schemas['TokenPairDTO']>('/platform/tenant-context', {
        method: 'POST',
        body: { tenantId },
      }),
    onSuccess: async (tokens) => {
      applySession(tokens);
      await queryClient.resetQueries();
    },
  });
}

export function useMyPermissions(): UseQueryResult<Schemas['MyPermissionsDTO']> {
  const tenantId = useSession((s) => s.tenantId);
  const setPermissions = useSession((s) => s.setPermissions);

  return useQuery({
    queryKey: queryKeys.permissions(tenantId),
    queryFn: async () => {
      const result = await request<Schemas['MyPermissionsDTO']>('/me/permissions');
      setPermissions(result.permissions);

      return result;
    },
    // Kiracı seçilmeden çalıştırılmaz: giriş jetonu tenant TAŞIMAZ (tid: null)
    // ve bu uçlar 403 döner — kullanıcı seçim yapmadan hata ekranına düşerdi
    // (SEOAI-78 incelemesi).
    enabled: tenantId !== null,
  });
}

export function useRoles(): UseQueryResult<Schemas['RoleDTO'][]> {
  const tenantId = useSession((s) => s.tenantId);

  return useQuery({
    queryKey: queryKeys.roles(tenantId),
    queryFn: () => request<Schemas['RoleDTO'][]>('/roles'),
    enabled: tenantId !== null,
  });
}

export function useProjects(): UseQueryResult<Schemas['ProjectDTO'][]> {
  const tenantId = useSession((s) => s.tenantId);

  return useQuery({
    queryKey: queryKeys.projects(tenantId),
    queryFn: () => request<Schemas['ProjectDTO'][]>('/projects'),
    enabled: tenantId !== null,
  });
}

export function useProject(id: string): UseQueryResult<Schemas['ProjectDTO']> {
  const tenantId = useSession((s) => s.tenantId);

  return useQuery({
    queryKey: queryKeys.project(tenantId, id),
    queryFn: () => request<Schemas['ProjectDTO']>(`/projects/${id}`),
    enabled: tenantId !== null,
  });
}

export function useLogin() {
  const applySession = useSession((s) => s.applySession);

  return useMutation({
    mutationFn: (credentials: { email: string; password: string }) =>
      request<Schemas['TokenPairDTO']>('/auth/login', { method: 'POST', body: credentials }),
    /*
     * Sunucu tek aktif üyelikte kurumu JETONA bağlar, yanıtta bildirir ve
     * seçilecek başka kurum olup olmadığını da söyler (SEOAI-80). Yanıt BİR
     * BÜTÜN olarak uygulanır; parçalarını ayrı ayrı yazmak, birini unutmaya
     * açık kapı bırakıyordu.
     */
    onSuccess: applySession,
  });
}

/**
 * Kurum değiştirme. Yeni token çifti aktif kurumu TAŞIR (SEOAI-22): sunucu
 * tarafı kiracıyı istemciden değil jetondan okur.
 *
 * Geçiş sonrası önbellek TÜMÜYLE temizlenir. Anahtarlar zaten kurum içerir, ama
 * temizlemek ikinci bir güvenlik hattıdır: kurum içermeyen bir anahtar ileride
 * eklenirse sızıntı burada durur.
 */
export function useSwitchTenant() {
  const queryClient = useQueryClient();
  const applySession = useSession((s) => s.applySession);

  return useMutation({
    mutationFn: (tenantId: string) =>
      request<Schemas['TokenPairDTO']>('/auth/switch-tenant', {
        method: 'POST',
        body: { tenantId },
      }),
    onSuccess: async (tokens) => {
      // Sunucunun bildirdiği kurum esastır; istek gövdesi yalnızca TALEPTİR.
      applySession(tokens);
      await queryClient.resetQueries();
    },
  });
}
